import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkshopDto } from '@workshop-hst/shared';
import { api, ApiError, clearAdminKey, getAdminKey, setAdminKey } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/Toast';
import { Avatar } from '../components/Avatar';
import { SearchBox } from '../components/SearchBox';
import { Tag } from '../components/Tag';

type Tab = 'all' | 'draft' | 'scheduled' | 'past' | 'canceled';

export function AdminPage() {
  const [hasKey, setHasKey] = useState(() => Boolean(getAdminKey()));
  if (!hasKey) return <AdminKeyPrompt onSet={() => setHasKey(true)} />;
  return <AdminWorkshopList onSignOut={() => setHasKey(false)} />;
}

function AdminKeyPrompt({ onSet }: { onSet: () => void }) {
  const [key, setKey] = useState('');
  return (
    <div className="container-page max-w-md py-24">
      <h1 className="text-2xl font-semibold tracking-tight">Admin sign in</h1>
      <p className="mt-2 text-sm text-ink-muted">Enter your password to continue.</p>
      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setAdminKey(key.trim());
          onSet();
        }}
      >
        <input
          type="password"
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value)}
          className="input"
          placeholder="Password"
        />
        <button type="submit" className="btn-primary w-full" disabled={key.trim().length === 0}>
          Continue
        </button>
      </form>
    </div>
  );
}

function AdminWorkshopList({ onSignOut }: { onSignOut: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'workshops'],
    queryFn: () => api.admin.listWorkshops(),
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'workshops'] });

  const publish = useMutation({
    mutationFn: (id: string) => api.admin.publishWorkshop(id),
    onSuccess: () => {
      toast.success('Published', 'Zoom + cover image ready.');
      invalidate();
    },
    onError: (err) => toast.error('Publish failed', err instanceof ApiError ? err.message : String(err)),
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.admin.unpublishWorkshop(id),
    onSuccess: () => {
      toast.success('Moved back to draft');
      invalidate();
    },
    onError: (err) => toast.error('Unpublish failed', err instanceof ApiError ? err.message : String(err)),
  });

  const workshops = query.data?.workshops ?? [];

  const counts = useMemo(() => {
    const c = { all: workshops.length, draft: 0, scheduled: 0, past: 0, canceled: 0 };
    for (const w of workshops) {
      if (w.status === 'draft') c.draft++;
      else if (w.status === 'scheduled' || w.status === 'live') c.scheduled++;
      else if (w.status === 'completed') c.past++;
      else if (w.status === 'canceled') c.canceled++;
    }
    return c;
  }, [workshops]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workshops.filter((w) => {
      if (tab === 'draft' && w.status !== 'draft') return false;
      if (tab === 'scheduled' && w.status !== 'scheduled' && w.status !== 'live') return false;
      if (tab === 'past' && w.status !== 'completed') return false;
      if (tab === 'canceled' && w.status !== 'canceled') return false;
      if (q && !w.title.toLowerCase().includes(q) && !w.tags.some((t) => t.toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  }, [workshops, tab, search]);

  if (query.isError) {
    const msg = query.error instanceof ApiError && query.error.status === 401
      ? 'Admin key was rejected. Sign out and try again.'
      : 'Failed to load admin workshops.';
    return (
      <div className="container-page py-16">
        <p className="text-sm text-red-600">{msg}</p>
        <button
          className="btn-secondary mt-4"
          onClick={() => {
            clearAdminKey();
            onSignOut();
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="container-page py-10">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-accent">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">Workshops</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            Create drafts, publish to provision Zoom + cover image, and manage past sessions.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/crm" className="btn-secondary">
            Host CRM
          </Link>
          <Link to="/admin/preview" className="btn-secondary">
            Social preview
          </Link>
          <Link to="/admin/new" className="btn-accent">
            <PlusIcon /> New workshop
          </Link>
          <button
            className="text-sm text-ink-muted hover:text-ink"
            onClick={() => {
              clearAdminKey();
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Tab strip + search */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-b border-line">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          <TabButton label="All" count={counts.all} active={tab === 'all'} onClick={() => setTab('all')} />
          <TabButton label="Drafts" count={counts.draft} active={tab === 'draft'} onClick={() => setTab('draft')} />
          <TabButton label="Scheduled" count={counts.scheduled} active={tab === 'scheduled'} onClick={() => setTab('scheduled')} />
          <TabButton label="Past" count={counts.past} active={tab === 'past'} onClick={() => setTab('past')} />
          <TabButton label="Canceled" count={counts.canceled} active={tab === 'canceled'} onClick={() => setTab('canceled')} />
        </div>
        <div className="pb-2">
          <SearchBox value={search} onChange={setSearch} />
        </div>
      </div>

      {/* List */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-canvas/60 text-[11px] uppercase tracking-wider text-ink-subtle">
            <tr>
              <th className="px-5 py-3 text-left font-semibold">Workshop</th>
              <th className="px-5 py-3 text-left font-semibold">Status</th>
              <th className="px-5 py-3 text-left font-semibold">When</th>
              <th className="px-5 py-3 text-left font-semibold">Recording</th>
              <th className="px-5 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filtered.map((w) => (
              <AdminRow
                key={w.id}
                w={w}
                onPublish={() => publish.mutate(w.id)}
                onUnpublish={() => unpublish.mutate(w.id)}
                publishing={publish.isPending && publish.variables === w.id}
                unpublishing={unpublish.isPending && unpublish.variables === w.id}
              />
            ))}
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center text-sm text-ink-subtle">
                  {workshops.length === 0
                    ? 'No workshops yet. Click "New workshop" to create your first draft.'
                    : 'No workshops match the current filter.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminRow({
  w,
  onPublish,
  onUnpublish,
  publishing,
  unpublishing,
}: {
  w: WorkshopDto;
  onPublish: () => void;
  onUnpublish: () => void;
  publishing: boolean;
  unpublishing: boolean;
}) {
  return (
    <tr className="group hover:bg-canvas/60">
      <td className="px-5 py-4">
        <Link to={`/admin/workshops/${w.id}`} className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-14 shrink-0 overflow-hidden rounded-lg bg-canvas">
            {w.coverImageUrl ? (
              <img src={w.coverImageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-accent-soft to-canvas" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium group-hover:text-accent">{w.title}</p>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-subtle">
              <Avatar name={w.hostName} url={w.hostHeadshotUrl} size={14} />
              <span>{w.hostName}</span>
              {w.guest ? <span className="text-ink-subtle">· w/ {w.guest.name}</span> : null}
              {w.tags[0] ? <Tag>{w.tags[0]}</Tag> : null}
            </div>
          </div>
        </Link>
      </td>
      <td className="px-5 py-4">
        <StatusPill status={w.status} />
      </td>
      <td className="px-5 py-4 text-ink-muted whitespace-nowrap">{formatDateTime(w.startsAt)}</td>
      <td className="px-5 py-4 text-ink-muted">
        {w.recordingPlayUrl ? (
          <a href={w.recordingPlayUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            ready
          </a>
        ) : w.recordingAvailable ? (
          <span className="text-xs text-accent">private</span>
        ) : w.status === 'completed' ? (
          <span className="text-xs">processing…</span>
        ) : (
          <span className="text-xs">—</span>
        )}
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-2">
          {w.status === 'draft' ? (
            <button className="btn-accent" disabled={publishing} onClick={onPublish}>
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
          ) : null}
          {w.status === 'scheduled' ? (
            <button className="btn-secondary" disabled={unpublishing} onClick={onUnpublish}>
              {unpublishing ? '…' : 'Unpublish'}
            </button>
          ) : null}
          <Link to={`/admin/workshops/${w.id}`} className="btn-secondary">
            Edit
          </Link>
          <Link to={`/admin/crm?workshop=${w.id}`} className="btn-secondary">
            CRM
          </Link>
        </div>
      </td>
    </tr>
  );
}

function TabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition ${
        active ? 'text-ink' : 'text-ink-muted hover:text-ink'
      }`}
    >
      <span>{label}</span>
      <span className={`tabular-nums text-xs ${active ? 'text-ink' : 'text-ink-subtle'}`}>{count}</span>
      {active ? <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-ink" /> : null}
    </button>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M7 2v10M2 7h10" strokeLinecap="round" />
    </svg>
  );
}
